import classNames from "classnames/bind";
import { IconProps, Placeholder } from "@phosphor-icons/react";
import React, { useRef } from "react";
import colours from "../constants/colours";
import styles from "./EmptyState.module.scss";
import type { EmptyStateProps } from "./types";
import { nanoid } from "nanoid";

const cx = classNames.bind(styles);

export const EmptyStateVariant = {
  DEFAULT: "default",
  ERROR: "error",
  WARNING: "warning",
  INFO: "info",
  SUCCESS: "success",
} as const;

const variantBackgroundColours = {
  [EmptyStateVariant.DEFAULT]: colours.colours.navy[50],
  [EmptyStateVariant.ERROR]: colours.colours.status.errorLight,
  [EmptyStateVariant.WARNING]: colours.colours.status.warningLight,
  [EmptyStateVariant.INFO]: colours.colours.status.infoLight,
  [EmptyStateVariant.SUCCESS]: colours.colours.status.successLight,
};

const variantColours = {
  [EmptyStateVariant.DEFAULT]: colours.colours.navy[300],
  [EmptyStateVariant.ERROR]: colours.colours.status.error,
  [EmptyStateVariant.WARNING]: colours.colours.status.warning,
  [EmptyStateVariant.INFO]: colours.colours.status.info,
  [EmptyStateVariant.SUCCESS]: colours.colours.status.success,
};

/**
 * Use this component when there is no data to show and you want to show a placeholder in its place with helpful hints or actions the user can take to get some data to show
 */
const EmptyState = ({
  title,
  suggestion,
  icon,
  variant = "default",
  size = "normal",
  action,
  isHidden = false,
  className,
  style,
}: EmptyStateProps) => {
  const titleId = useRef(nanoid());
  const defaultIconProps: IconProps = {
    size: size === "normal" ? 28 : 36,
    color: variantColours[variant],
    weight: "thin",
    alt: title,
  };

  const hintIcon = icon ? (
    React.cloneElement(icon, {
      ...defaultIconProps,
      ...icon.props,
    })
  ) : (
    <Placeholder
      {...defaultIconProps}
      data-is-default
    />
  );

  return (
    <div
      className={cx(className, "container")}
      hidden={isHidden}
      role="status"
      aria-labelledby={titleId.current}
      style={style}
    >
      <div
        className={cx("iconContainer", size)}
        style={{ backgroundColor: variantBackgroundColours[variant] }}
      >
        {hintIcon}
      </div>

      <p
        className={cx("title", size)}
        id={titleId.current}
      >
        {title}
      </p>

      {suggestion ? (
        typeof suggestion === "string" ? (
          <p className={cx("suggestion", size)}>{suggestion}</p>
        ) : typeof suggestion === "function" ? (
          suggestion({ className: cx("suggestion", size) })
        ) : null
      ) : null}

      <div className={cx("action")}>{action ? action : null}</div>
    </div>
  );
};

export default EmptyState;
